import moment from "moment";

// Administration > Sites: fixed lists, form <-> API mappers and the form's
// client checks. The server checks every save again.

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export const SITE_TYPES = [
  { value: "FACTORY", label: "Factory" },
  { value: "OFFICE", label: "Office" },
  { value: "WAREHOUSE", label: "Warehouse" },
  { value: "BRANCH", label: "Branch" },
  { value: "OTHER", label: "Other" },
];
export const siteTypeLabel = (value) => SITE_TYPES.find((type) => type.value === value)?.label || value || "—";

export const BODY_TYPES = [
  { value: "CORPORATION", label: "Corporation" },
  { value: "MUNICIPALITY", label: "Municipality" },
  { value: "TOWN_PANCHAYAT", label: "Town panchayat" },
  { value: "VILLAGE_PANCHAYAT", label: "Village panchayat" },
  { value: "OTHER", label: "Other" },
];

export const GEOFENCE_RADII = [100, 200, 500, 1000].map((value) => ({ value: String(value), label: `${value} m` }));

export const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "-name", label: "Name (Z–A)" },
  { value: "code", label: "Code (A–Z)" },
  { value: "-code", label: "Code (Z–A)" },
  { value: "site_type", label: "Type (A–Z)" },
  { value: "-site_type", label: "Type (Z–A)" },
  { value: "state", label: "State (A–Z)" },
  { value: "-state", label: "State (Z–A)" },
  { value: "-employee_count", label: "Employees (most first)" },
  { value: "employee_count", label: "Employees (fewest first)" },
];

// Statutory config pages for the coverage "Set up" links (one page,
// item = pf | esi | pt | lwf).
export const SETUP_ROUTES = {
  PF: "/admin/payroll/configuration/statutory-configuration?item=pf",
  ESI: "/admin/payroll/configuration/statutory-configuration?item=esi",
  PT: "/admin/payroll/configuration/statutory-configuration?item=pt",
  LWF: "/admin/payroll/configuration/statutory-configuration?item=lwf",
};

export const siteHeaders = [
  { name: "Site Name", key: "name", type: "string" },
  { name: "Site Code", key: "code", type: "string" },
  { name: "Status", key: "status_label", type: "string" },
  { name: "Type", key: "site_type_label", type: "string" },
  { name: "City / Local Body", key: "local_body", type: "string" },
  { name: "State", key: "state", type: "string" },
  { name: "Companies", key: "companies_label", type: "string" },
  { name: "Employees", key: "employee_count", type: "number", align: "right" },
];

export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "—");

export const percentOf = (count, total) => (total ? Math.round((count / total) * 100) : 0);

// API list row -> table row (labels ready to show and export).
export const fromApiSiteRow = (row) => ({
  ...row,
  status_label: row.is_active ? "Active" : "Inactive",
  site_type_label: siteTypeLabel(row.site_type),
  companies: row.companies || [],
  companies_label: (row.companies || []).map((company) => company.name).join(", "),
  employee_count: Number(row.employee_count) || 0,
});

// "Karthik R · 98430 12345" <-> contact_name / contact_phone. The phone is
// the run of digits (with spaces, + or -) at the end; the rest is the name.
export const splitContact = (text) => {
  const value = String(text || "").trim();
  if (!value) return { contact_name: "", contact_phone: "" };
  const match = /^(.*?)[\s,·|/-]*(\+?\d[\d\s-]{4,}\d)$/.exec(value);
  if (!match) return { contact_name: value, contact_phone: "" };
  return { contact_name: match[1].replace(/[\s,·|/-]+$/, "").trim(), contact_phone: match[2].trim() };
};

export const joinContact = (name, phone) => [name, phone].filter((part) => part && String(part).trim()).join(" · ");

// "11.341036, 77.717163" -> { latitude, longitude } (strings), or null for
// blank. { error } when it doesn't parse or is out of range.
export const parseLatLong = (text) => {
  const value = String(text || "").trim();
  if (!value) return null;
  const parts = value.split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2 || parts.some((part) => !Number.isFinite(Number(part)))) {
    return { error: "Enter latitude, longitude (e.g. 11.3410, 77.7172)" };
  }
  const [latitude, longitude] = parts.map(Number);
  if (latitude < -90 || latitude > 90) return { error: "Latitude must be between -90 and 90" };
  if (longitude < -180 || longitude > 180) return { error: "Longitude must be between -180 and 180" };
  return { latitude: parts[0], longitude: parts[1] };
};

export const emptySiteForm = () => ({
  name: "",
  code: "",
  site_type: "FACTORY",
  active_from: moment().format("YYYY-MM-DD"),
  is_active: true,
  address_line1: "",
  address_line2: "",
  pincode: "",
  state: "",
  district: null, // { id, name }
  local_body: null, // { id, name, body_type }
  location: "",
  geofence_radius_m: "",
  contact: "",
});

export const siteToForm = (site) => ({
  name: site.name || "",
  code: site.code || "",
  site_type: site.site_type || "FACTORY",
  active_from: site.active_from || "",
  is_active: Boolean(site.is_active),
  address_line1: site.address_line1 || "",
  address_line2: site.address_line2 || "",
  pincode: site.pincode || "",
  state: site.state || "",
  district: site.district || null,
  local_body: site.local_body || null,
  location: site.latitude != null && site.longitude != null ? `${site.latitude}, ${site.longitude}` : "",
  geofence_radius_m: site.geofence_radius_m ? String(site.geofence_radius_m) : "",
  contact: joinContact(site.contact_name, site.contact_phone),
});

export const validateSiteForm = (form) => {
  const errors = {};
  const name = form.name.trim();
  const code = form.code.trim();
  if (!name) errors.name = "Enter the site name";
  else if (name.length > 150) errors.name = "Up to 150 characters";
  if (!code) errors.code = "Enter the site code";
  else if (code.length > 20) errors.code = "Up to 20 characters";
  else if (/\s/.test(code)) errors.code = "No spaces in the code";
  if (!form.site_type) errors.site_type = "Pick the type";
  if (!form.active_from) errors.active_from = "Pick the date";
  if (!form.address_line1.trim()) errors.address_line1 = "Enter the address";
  if (!/^\d{6}$/.test(form.pincode)) errors.pincode = form.pincode ? "PIN code must be 6 digits" : "Enter the PIN code";
  if (!form.state) errors.state = "Pick the state";
  if (!form.district) errors.district = "Pick the district";
  if (!form.local_body) errors.local_body = "Pick the city / local body";
  const location = parseLatLong(form.location);
  if (location?.error) errors.location = location.error;
  if (location && !location.error && !form.geofence_radius_m) errors.geofence_radius_m = "Pick the geofence radius";
  return errors;
};

export const formToApi = (form) => {
  const location = parseLatLong(form.location);
  const contact = splitContact(form.contact);
  return {
    name: form.name.trim(),
    code: form.code.trim(),
    site_type: form.site_type,
    active_from: form.active_from,
    is_active: Boolean(form.is_active),
    address_line1: form.address_line1.trim(),
    address_line2: form.address_line2.trim(),
    pincode: form.pincode,
    state: form.state,
    district_id: form.district?.id ?? null,
    local_body_id: form.local_body?.id ?? null,
    latitude: location && !location.error ? location.latitude : null,
    longitude: location && !location.error ? location.longitude : null,
    geofence_radius_m: location && form.geofence_radius_m ? Number(form.geofence_radius_m) : null,
    ...contact,
  };
};

// Server field -> form field (for errors under the right input).
export const SERVER_FIELDS = {
  name: "name",
  code: "code",
  site_type: "site_type",
  active_from: "active_from",
  address_line1: "address_line1",
  address_line2: "address_line2",
  pincode: "pincode",
  state: "state",
  district_id: "district",
  local_body_id: "local_body",
  latitude: "location",
  longitude: "location",
  geofence_radius_m: "geofence_radius_m",
  contact_name: "contact",
  contact_phone: "contact",
};

export const splitServerErrors = (error) => {
  const fields = {};
  const general = [];
  Object.entries(error?.fieldErrors || {}).forEach(([key, message]) => {
    if (SERVER_FIELDS[key]) fields[SERVER_FIELDS[key]] = fields[SERVER_FIELDS[key]] || message;
    else general.push(message);
  });
  if (!Object.keys(fields).length && !general.length) general.push(error?.message || "Could not save. Please try again.");
  return { fields, general: general.join(" ") };
};

// Fields changed since the form opened (for "Discard changes?").
export const formChanged = (initial, form) => JSON.stringify(initial) !== JSON.stringify(form);
